// A18 JIT / break-glass access — module-local data layer. REAL backend
// (/admin/jit/*). Request → four-eyes approve → time-boxed grant; break-glass =
// emergency self-grant (audited). Enforcement is flag-gated server-side.
import { api } from '@/lib/api'

export interface AccessRequest {
  id: number
  requester: string
  scope: string
  reason: string
  status: string
  approver: string
  approved_at: string | null
  expires_at: string | null
  created_at: string
  active: boolean
}
export interface BreakGlassEvent {
  id: number
  admin: string
  scope: string
  reason: string
  expires_at: string
  revoked_at: string | null
  created_at: string
  active: boolean
}

const opt = (s?: AbortSignal) => (s ? { signal: s } : undefined)

export const fetchRequests = (status = '', s?: AbortSignal) =>
  api.get<{ requests: AccessRequest[] }>('/admin/jit/requests', { query: status ? { status } : {}, ...(s ? { signal: s } : {}) }).then((d) => d.requests ?? [])
export const requestAccess = (scope: string, reason: string) =>
  api.post<{ id: number }>('/admin/jit/requests', { scope, reason })
export const approveRequest = (id: number, duration_mins: number) =>
  api.post(`/admin/jit/requests/${id}/approve`, { duration_mins })
export const denyRequest = (id: number) => api.post(`/admin/jit/requests/${id}/deny`, {})
export const revokeRequest = (id: number) => api.post(`/admin/jit/requests/${id}/revoke`, {})

export const fetchBreakGlass = (s?: AbortSignal) =>
  api.get<{ events: BreakGlassEvent[] }>('/admin/jit/break-glass', opt(s)).then((d) => d.events ?? [])
export const activateBreakGlass = (scope: string, reason: string, duration_mins: number) =>
  api.post<{ id: number }>('/admin/jit/break-glass', { scope, reason, duration_mins })

// Common sensitive scopes (free text also allowed).
export const SCOPES = ['db_browser', 'finance', 'user_pii', 'moderation', 'deploy', 'staff'] as const

export const JIT_STR = {
  title: 'JIT доступ',
  desc: 'Запрос временного привилегированного доступа (four-eyes) + авто-истечение',
  bgTitle: 'Break-glass (аварийный доступ)',
  bgDesc: 'Немедленный доступ без одобрения — под усиленным аудитом',
  request: 'Запросить доступ',
  scope: 'Область (scope)',
  reason: 'Причина',
  duration: 'Срок (мин)',
  approve: 'Одобрить',
  deny: 'Отклонить',
  revoke: 'Отозвать',
  activate: 'Активировать',
  requester: 'Запросил',
  approver: 'Одобрил',
  status: 'Статус',
  expires: 'Истекает',
  active: 'Активен',
  pending: 'Ожидает',
  approved: 'Одобрен',
  denied: 'Отклонён',
  revoked: 'Отозван',
  expired: 'Истёк',
  fourEyes: 'Four-eyes: нельзя одобрить свой запрос',
  admin: 'Админ',
  done: 'Готово',
  failed: 'Ошибка',
  empty: 'Нет запросов',
  emptyBg: 'Нет событий break-glass',
  refresh: 'Обновить',
  all: 'Все',
  activeGrants: 'Активные гранты',
} as const

export const REQ_STATUS_LABEL: Record<string, string> = {
  pending: JIT_STR.pending,
  approved: JIT_STR.approved,
  denied: JIT_STR.denied,
  revoked: JIT_STR.revoked,
  expired: JIT_STR.expired,
}
